import { useRef, useState } from "react";
import { Heart, Star, Gift, Crown, Music, Camera, Sparkles, Coffee, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { playCoin, playTick, playFanfare, confetti } from "@/lib/celebrate";
import { useSkill, SkillInfo, SkillTop, SkillResult } from "./SkillShell";

const ICONS = [Heart, Star, Gift, Crown, Music, Camera, Sparkles, Coffee];
const TINTS = ["text-rose-500", "text-amber-500", "text-pink-500", "text-yellow-500", "text-fuchsia-500", "text-sky-500", "text-violet-500", "text-orange-500"];
const RULES = ["Decke immer zwei Karten auf – passen sie zusammen, bleiben sie offen.", "Finde alle 8 Paare mit möglichst wenigen Zügen.", "Ein Zug = zwei aufgedeckte Karten."];

function Card({ i, sym, open, matched, onFlip }) {
  const Icon = sym != null ? ICONS[sym] : null;
  return (
    <button type="button" className="flip-wrap aspect-square" onClick={() => onFlip(i)} data-testid={`memory-card-${i}`} data-open={open || undefined} aria-label={`Karte ${i + 1}`}>
      <span className={`flip-card ${open ? "is-open" : ""} ${matched ? "is-matched" : ""}`}>
        <span className="flip-face pink-gradient shadow-md shadow-rose-200"><Heart className="w-6 h-6 text-white/80" fill="currentColor" /></span>
        <span className="flip-face flip-back bg-white border border-pink-100 shadow-md">{Icon && <Icon className={`w-8 h-8 ${TINTS[sym]}`} strokeWidth={2.2} />}</span>
      </span>
    </button>
  );
}

export function MemoryGame() {
  const { ov, load } = useSkill();
  const [s, setS] = useState(null);
  const [result, setResult] = useState(null);
  const busy = useRef(false);
  const start = async () => {
    try {
      const { data } = await api.post("/skill/memory/start");
      setResult(null);
      setS({ id: data.id, syms: {}, open: [], matched: [], moves: 0 });
    } catch (e) { toast.error(formatErr(e)); }
  };
  const flip = async (i) => {
    if (!s || busy.current || s.open.includes(i) || s.matched.includes(i) || s.open.length >= 2) return;
    busy.current = true;
    try {
      const { data } = await api.post(`/skill/memory/${s.id}/flip`, { idx: i });
      playTick();
      setS((p) => ({ ...p, syms: { ...p.syms, [i]: data.symbol }, open: [...p.open, i], moves: data.moves }));
      if (data.match === true) {
        playCoin(3);
        setS((p) => ({ ...p, matched: [...p.matched, i, data.other], open: [] }));
      } else if (data.match === false) {
        await new Promise((r) => setTimeout(r, 750));
        setS((p) => ({ ...p, open: [] }));
      }
      if (data.done) {
        playFanfare(); confetti({ count: 90 });
        setResult(data.result); load();
      }
    } catch (e) { toast.error(formatErr(e)); } finally { busy.current = false; }
  };
  if (!ov) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  const g = ov.games.memory;
  return (
    <div className="space-y-4" data-testid="memory-game">
      <SkillInfo game="memory" g={g} rules={RULES} />
      {result ? <SkillResult game="memory" result={result} onAgain={start} /> : !s ? (
        <button type="button" onClick={start} data-testid="memory-start" className="tap w-full h-14 rounded-full pink-gradient text-white font-extrabold text-lg pink-glow inline-flex items-center justify-center gap-2"><Play className="w-5 h-5" fill="currentColor" /> Spiel starten</button>
      ) : (
        <div className="lux-card rounded-3xl p-4">
          <div className="flex items-center justify-between mb-3 text-sm font-bold text-slate-600">
            <span data-testid="memory-moves">Züge: <span className="tabular-nums text-rose-600">{s.moves}</span></span>
            <span data-testid="memory-pairs">Paare: <span className="tabular-nums text-rose-600">{s.matched.length / 2}/8</span></span>
          </div>
          <div className="grid grid-cols-4 gap-2.5" data-testid="memory-board">
            {Array.from({ length: 16 }, (_, i) => <Card key={i} i={i} sym={s.syms[i]} open={s.open.includes(i) || s.matched.includes(i)} matched={s.matched.includes(i)} onFlip={flip} />)}
          </div>
        </div>
      )}
      <SkillTop game="memory" g={g} />
    </div>
  );
}
